// Native push notifications (FCM) for the Android app.
//
// Three rules shape this file:
//
//   1. NATIVE ONLY. Web gets no push at all — email stays the web channel.
//      Every export is a no-op off-native, so callers never need to guard.
//      The plugin is dynamically imported so @capacitor/push-notifications
//      never lands in the web bundle's critical path either.
//
//   2. NEVER ASK ON LAUNCH. attachPushListeners() registers listeners and
//      re-registers an ALREADY-GRANTED device, and that is all it does. The
//      permission prompt only ever comes from enablePush(), which is wired to
//      a deliberate user action (Settings -> Notifications "Turn on push", or
//      the in-context ask the first time someone opens Messages).
//
//   3. SAYING NO CHANGES NOTHING. Every failure path here is swallowed and
//      logged. The app must work identically for someone who declines, and no
//      push failure can ever block a screen from rendering.
//
// The token lands in userProfiles/{uid}.fcmTokens via the registerPushToken
// callable — that collection is not client-writable (firestore.rules grants
// self-READ only), which is deliberate: it also holds the `permissions` grant
// map the route guard reads.
import { Capacitor, registerPlugin } from '@capacitor/core';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';

// Must match functions/index.js's PUSH_CHANNEL_ID and the
// default_notification_channel_id meta-data in AndroidManifest.xml. All three
// have to agree or a backgrounded push lands in a silent fallback channel
// with no heads-up pop-up.
const CHANNEL_ID = 'roofbeam-default';

// Local plugin (android/.../AppNotifications.java). Answers the question
// @capacitor/push-notifications cannot: are notifications actually allowed at
// the OS level, and take me to the screen where that is changed.
const AppNotifications = registerPlugin('AppNotifications');

// True when the OS allows notifications for RoofBeam. Defaults to TRUE when
// the plugin isn't present (an older installed build) so an unknown answer
// never blocks the normal request flow.
export async function areNotificationsEnabled() {
  try {
    if (!Capacitor.isPluginAvailable('AppNotifications')) return true;
    const res = await AppNotifications.areEnabled();
    return !!(res && res.enabled);
  } catch {
    return true;
  }
}

// Opens RoofBeam's own Android notification settings screen. Returns false
// when the running build has no such plugin, so the UI can fall back to
// spelling out the path instead of offering a button that does nothing.
export async function openNotificationSettings() {
  try {
    if (!Capacitor.isPluginAvailable('AppNotifications')) return false;
    await AppNotifications.openSettings();
    return true;
  } catch (e) {
    console.error('openNotificationSettings failed:', e?.message || e);
    return false;
  }
}

// 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unsupported'
export const PUSH_UNSUPPORTED = 'unsupported';

export function isPushSupported() {
  try {
    // Android only for now — iOS push needs an APNs key and a provisioning
    // profile that this project does not have yet.
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return false;
    // AND the plugin has to actually be COMPILED INTO the running APK. An
    // installed build older than the push release has no PushNotifications
    // plugin, so every call throws "not implemented" — which previously meant
    // the Settings toggles sat there doing nothing at all. isPluginAvailable
    // is what separates "this device can't do push" from "this BUILD can't".
    return Capacitor.isPluginAvailable('PushNotifications');
  } catch {
    return false;
  }
}

// True when we're on the native Android app but the running build predates
// push — i.e. the fix is "install the latest app", not "you're on the web".
// Drives the distinct message in Settings -> Notifications.
export function isPushMissingFromBuild() {
  try {
    return Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === 'android'
      && !Capacitor.isPluginAvailable('PushNotifications');
  } catch {
    return false;
  }
}

// BUG FIX (found by running v2 on the tablet, logcat):
//   Uncaught (in promise) Error: "PushNotifications.then()" is not implemented on android
// A Capacitor plugin handle is a Proxy that forwards EVERY property access to a
// native method. Returning it from an async function therefore breaks: the
// promise machinery probes `.then` on the resolved value, which the proxy turns
// into a native call to a `then()` method that does not exist. The whole of
// attachPushListeners() threw at its first line, so listeners never attached,
// no token ever registered, and "Turn on push" reported "Notifications are
// blocked" — the one thing it must never say when they are not.
// Wrapping the handle in a plain object keeps it away from `await`.
async function plugin() {
  const mod = await import('@capacitor/push-notifications');
  return { PushNotifications: mod.PushNotifications };
}

// ── deep-link plumbing ───────────────────────────────────────────────────────
// A tap can arrive before React Router is mounted (cold start straight from
// the lock screen), so the route is buffered here and drained by
// usePushNotifications() once it has a navigate().
let pendingRoute = null;
let routeHandler = null;

export function setPushRouteHandler(fn) {
  routeHandler = fn;
  if (fn && pendingRoute) {
    const r = pendingRoute;
    pendingRoute = null;
    // Defer a tick so navigation never runs inside the subscriber's own render.
    setTimeout(() => { try { fn(r); } catch (e) { console.error('push route handler failed:', e); } }, 0);
  }
}

function deliverRoute(route) {
  const r = typeof route === 'string' && route.startsWith('/') ? route : null;
  if (!r) return;
  if (routeHandler) {
    try { routeHandler(r); } catch (e) { console.error('push route handler failed:', e); }
  } else {
    pendingRoute = r;
  }
}

// ── token registration ──────────────────────────────────────────────────────
// The plugin's `registration` event can fire before Firebase Auth has restored
// the session, and registerPushToken is an authenticated callable — so an
// early token is held and flushed on the next auth state change.
let pendingToken = null;
let authUnsub = null;

async function sendTokenToServer(token) {
  if (!token) return;
  if (!auth.currentUser) {
    pendingToken = token;
    if (!authUnsub) {
      authUnsub = auth.onAuthStateChanged(u => {
        if (u && pendingToken) {
          const t = pendingToken;
          pendingToken = null;
          sendTokenToServer(t);
        }
      });
    }
    return;
  }
  try {
    await httpsCallable(functions, 'registerPushToken')({ token, platform: Capacitor.getPlatform() });
  } catch (e) {
    // Non-fatal: the user simply won't get push until the next successful
    // registration attempt (every app start re-fires `registration`).
    console.error('registerPushToken failed:', e?.message || e);
  }
}

// ── listeners ───────────────────────────────────────────────────────────────
let listenersAttached = false;

// Safe to call on every app start. Registers the listeners and, for a device
// that has ALREADY granted permission, re-registers its token (FCM rotates
// tokens, and a reinstall gets a new one). Never prompts.
export async function attachPushListeners() {
  if (!isPushSupported() || listenersAttached) return;
  listenersAttached = true;
  try {
    const { PushNotifications } = await plugin();

    await PushNotifications.addListener('registration', ({ value }) => {
      sendTokenToServer(value);
    });

    await PushNotifications.addListener('registrationError', err => {
      console.error('push registrationError:', err?.error || err);
    });

    // Foreground delivery. Android shows nothing by itself while the app is
    // open; the in-app UI is already live data, so this is logged and left —
    // deliberately NOT turned into an interrupting modal over whatever the
    // user is doing.
    await PushNotifications.addListener('pushNotificationReceived', n => {
      console.log('push received (foreground):', n?.title, n?.body);
    });

    // The tap. data.route is set by functions/index.js's sendPushToUids.
    await PushNotifications.addListener('pushNotificationActionPerformed', action => {
      deliverRoute(action?.notification?.data?.route);
    });

    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'granted') {
      await ensureChannel(PushNotifications);
      await PushNotifications.register();
    }
  } catch (e) {
    console.error('attachPushListeners failed:', e?.message || e);
  }
}

async function ensureChannel(PushNotifications) {
  try {
    // importance 5 (HIGH) is what produces a heads-up banner and a
    // lock-screen pop-up rather than a silent status-bar entry.
    // visibility 1 (PUBLIC) shows the content on the lock screen.
    await PushNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'RoofBeam alerts',
      description: 'Messages, tasks, signatures, leads and approvals',
      importance: 5,
      visibility: 1,
      sound: 'default',
      vibration: true,
      lights: true,
    });
  } catch (e) {
    console.error('createChannel failed:', e?.message || e);
  }
}

// ── public controls (Settings / the in-context ask) ─────────────────────────

// 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unsupported'
export async function getPushPermission() {
  if (!isPushSupported()) return PUSH_UNSUPPORTED;
  try {
    const { PushNotifications } = await plugin();
    const perm = await PushNotifications.checkPermissions();
    return perm.receive;
  } catch {
    return PUSH_UNSUPPORTED;
  }
}

// THE ONLY PLACE THAT PROMPTS. Call from a real user action.
// Returns { ok, state } — ok true only when a token registration was kicked
// off. A denied prompt is a normal outcome, not an error.
// Returns { ok, state, canOpenSettings?, error? }. EVERY branch returns
// something the UI can SAY — a tap must never be able to do nothing:
//   'granted'      registered (or already was)
//   'blocked'      the OS has notifications off for RoofBeam. Covers both
//                  "switched off in Settings" and "tapped Deny on the dialog",
//                  because Android reports those identically and the route
//                  back is the same screen either way.
//   'error'        the plugin itself failed — not the user's doing
//   'unsupported'
//
// BUG FIX (owner: "tapping Turn on push did nothing"): this used to call
// requestPermissions() blind. When Android has notifications switched OFF for
// the app, that returns 'denied' INSTANTLY and shows no dialog — so from the
// outside the tap did nothing visible, and the best the UI could then do was
// print a line of instructions telling him to go and find the right settings
// screen himself. It now reports 'blocked' distinctly and the caller gets a
// button that opens RoofBeam's notification settings directly.
export async function enablePush() {
  if (!isPushSupported()) return { ok: false, state: PUSH_UNSUPPORTED };
  try {
    const { PushNotifications } = await plugin();

    // Already allowed? Nothing to ask — just register. This is the path the
    // owner lands on now that he has turned notifications on by hand.
    const existing = await PushNotifications.checkPermissions();
    if (existing.receive === 'granted') {
      await ensureChannel(PushNotifications);
      await PushNotifications.register();
      return { ok: true, state: 'granted' };
    }

    // On Android 13+ this surfaces the POST_NOTIFICATIONS dialog. On 12 and
    // below it resolves 'granted' without one.
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive === 'granted') {
      await ensureChannel(PushNotifications);
      await PushNotifications.register();  // -> 'registration' -> registerPushToken
      return { ok: true, state: 'granted' };
    }

    return {
      ok: false,
      state: 'blocked',
      canOpenSettings: Capacitor.isPluginAvailable('AppNotifications'),
      osEnabled: await areNotificationsEnabled(),
    };
  } catch (e) {
    // An EXCEPTION is not a denial. Reporting 'denied' here is what made the
    // proxy bug above present itself as "Notifications are blocked for
    // RoofBeam" — sending the owner to system settings to fix something that
    // was not broken there. A distinct state so the UI can say what actually
    // happened.
    const msg = e?.message || String(e);
    console.error('enablePush failed:', msg);
    return { ok: false, state: 'error', error: msg };
  }
}

// Drops this user's tokens server-side. The OS-level permission is left alone
// (only the user can revoke that, in system settings) — this is the in-app
// "stop sending me push" switch.
export async function disablePush({ allDevices = false } = {}) {
  if (!isPushSupported()) return { ok: false };
  try {
    let token = null;
    if (!allDevices) {
      // There is no getToken() in the plugin API, so a single-device opt-out
      // would need the token we last registered. Simpler and more predictable
      // for the user: clear every device for this account.
      token = null;
    }
    await httpsCallable(functions, 'unregisterPushToken')({ token });
    return { ok: true };
  } catch (e) {
    console.error('disablePush failed:', e?.message || e);
    return { ok: false };
  }
}
